import { resourceIconUrl } from "../portraits";
import { paintedIconHtml, paintedIconUrl } from "../featureIcons";

/**
 * The economy's icons (FINISH_PLAN WP5): the gem, the Mark and a gem heap per pack size are the painted icons
 * (ui/featureIcons.ts, WP12); the Tribute coffer's coin stack is still drawn here as inline SVG, in the kit's palette.
 * Markup only, no rules. Gradient ids are shared by every copy of an icon (identical definitions).
 */

const GEM_DEFS = `<defs><linearGradient id="econGemA" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffb3dc"/><stop offset=".45" stop-color="#f0418f"/><stop offset="1" stop-color="#8c0f47"/></linearGradient>`
  + `<linearGradient id="econGemB" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ffe4f2"/><stop offset="1" stop-color="#ff6fae"/></linearGradient></defs>`;

/** One cut gem (crown and pavilion facets) in a 24×24 box. */
const GEM_BODY = `<path d="M12 22.2 1.8 9.2 6.2 3h11.6l4.4 6.2z" fill="url(#econGemA)" stroke="#5c0a2f" stroke-width="1.3" stroke-linejoin="round"/>`
  + `<path d="M8.2 3h7.6l2.2 6.2H6z" fill="url(#econGemB)" opacity=".9"/>`
  + `<path d="M1.8 9.2h20.4M6 9.2 12 22.2 18 9.2M9.6 9.2 12 22.2 14.4 9.2M6.2 3 8.2 9.2 12 3l3.8 6.2 2-6.2" fill="none" stroke="#5c0a2f" stroke-width=".7" stroke-linejoin="round" opacity=".55"/>`
  + `<path d="M7.4 4.6 9 4.6 7.8 7.6z" fill="#fff" opacity=".85"/>`;

/** The Shop's currency: a cut ruby-pink gem (never Gold's coins). */
export function gemIcon(size = 20): string { return paintedIconHtml("gem", size, "econ gem"); }

/** Gold, the upgrade currency (owner 2026-10-23: supplies are bought for it): the HQ coin pile. */
export function goldIcon(size = 20): string { return `<img class="econ gold" decoding="async" src="${resourceIconUrl("gold")}" alt="" width="${size}" height="${size}">`; }

/** A Mark: an octagonal bronze trade token stamped with the trader's scales (never the round gold coin). */
export function marksIcon(size = 20): string { return paintedIconHtml("marks", size, "econ mark"); }

/** Trophies (MP-23): a round crimson buckler with a gilt laurel on crossed swords, the Trophy Hall's earned currency. */
export function trophiesIcon(size = 20): string { return paintedIconHtml("trophies", size, "econ trophy"); }

/** The currency's icon as one URL: a hub pill's image and the fly-to-pill sprite. */
export function currencyIconUrl(currency: "gems" | "marks" | "trophies" | "gold"): string { return currency === "gold" ? resourceIconUrl("gold") : paintedIconUrl(currency === "gems" ? "gem" : currency === "marks" ? "marks" : "trophies"); }

/** A stack of gold coins with a gem on top: the fortress's Tribute coffer. */
export function coinsSvg(size = 22): string {
  const coin = (y: number) => `<ellipse cx="12" cy="${y + 2}" rx="8" ry="3" fill="#9a6414"/><ellipse cx="12" cy="${y}" rx="8" ry="3" fill="url(#econMarkA)" stroke="#6a3f0b" stroke-width=".9"/>`;
  return `<svg class="econ coins" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true"><defs><radialGradient id="econMarkA" cx=".35" cy=".3" r=".8"><stop offset="0" stop-color="#fff3c4"/><stop offset=".55" stop-color="#e6ae3e"/><stop offset="1" stop-color="#8f5a12"/></radialGradient></defs>`
    + `${coin(18)}${coin(14)}${coin(10)}<g transform="translate(9.5 1.5) scale(.42)">${GEM_BODY}</g>${GEM_DEFS}</svg>`;
}

/** The largest gem-pack art index (gem_pack_1..4: one stone, a few, a heap, a hoard). */
const GEM_PACK_TIERS = 4;

/** A heap of gems for a gem pack: more stones for a bigger pack (tiers 0–3). */
export function gemPackIcon(tier: number, size = 56): string {
  const n = Math.max(1, Math.min(GEM_PACK_TIERS, Math.round(tier) + 1)) as 1 | 2 | 3 | 4;
  return paintedIconHtml(`gemPack${n}`, size, "econ pile");
}

/** Inline amount with its currency icon: `💎 250` in the kit's style. */
export function priceHtml(currency: "gems" | "marks" | "trophies", amount: number | string, size = 16): string {
  return `<span class="price ${currency}">${currency === "gems" ? gemIcon(size) : currency === "marks" ? marksIcon(size) : trophiesIcon(size)}<b>${amount}</b></span>`;
}
